import classNames from "classnames";
import ReactMarkdown from "react-markdown";
import { useLocale } from "../../contexts/LocaleContext";
import Footer from "../Footer/Footer";
import SiteNav from "../SiteNav/SiteNav";
import Seo from "../Seo/Seo";
import styles from "./PolicyLayout.module.scss";

type PolicyFact = {
  label: string;
  title: string;
  body: string;
};

type PolicySection = {
  id: string;
  label: string;
};

type PolicyLayoutProps = {
  badge: string;
  title: string;
  pageTitle: string;
  canonicalPath: string;
  description: string;
  summaryTitle: string;
  summaryBody: string;
  facts?: PolicyFact[];
  sections: PolicySection[];
  content: string;
};

export default function PolicyLayout({
  badge,
  title,
  pageTitle,
  canonicalPath,
  description,
  summaryTitle,
  summaryBody,
  facts,
  sections,
  content,
}: PolicyLayoutProps) {
  let sectionIndex = 0;
  const hasFacts = Boolean(facts?.length);
  const { messages } = useLocale();

  return (
    <main className={styles.page}>
      <Seo title={pageTitle} description={description} path={canonicalPath} />

      <SiteNav solidThreshold={320} />

      <section className={styles.hero}>
        <div className={styles.container}>
          <div className={classNames(styles.heroInner, !hasFacts && styles.heroInnerWide)}>
            <div className={classNames(styles.heroCopy, !hasFacts && styles.heroCopyWide)}>
              <span className={styles.badge}>{badge}</span>
              <h1>{title}</h1>
              <p>{description}</p>
              <a
                className={styles.contactLink}
                href={`mailto:info@catlogic.tech?subject=${encodeURIComponent(messages.policyLayout.legalRequestSubject)}`}
              >
                info@catlogic.tech
              </a>
            </div>

            {facts && facts.length > 0 ? (
              <div className={styles.factGrid}>
                {facts.map((fact) => (
                  <article key={fact.title} className={styles.factCard}>
                    <span>{fact.label}</span>
                    <strong>{fact.title}</strong>
                    <p>{fact.body}</p>
                  </article>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </section>

      <div className={styles.surface}>
        <div className={styles.container}>
          <section className={styles.documentSection}>
            <div className={styles.documentIntro}>
              <span className={styles.documentLabel}>{messages.policyLayout.documentLabel}</span>
              <h2>{summaryTitle}</h2>
              <p>{summaryBody}</p>

              <div className={styles.documentNav}>
                <span className={styles.documentNavLabel}>{messages.policyLayout.sectionsLabel}</span>
                <div className={styles.documentLinks}>
                  {sections.map((section) => (
                    <a key={section.id} className={styles.documentLink} href={`#${section.id}`}>
                      {section.label}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <article className={styles.document}>
              <ReactMarkdown
                components={{
                  h2: ({ node: _node, children, ...props }) => {
                    const currentSection = sections[sectionIndex];
                    const headingId = currentSection?.id ?? `section-${sectionIndex + 1}`;

                    sectionIndex += 1;

                    return (
                      <h2 id={headingId} {...props}>
                        {children}
                      </h2>
                    );
                  },
                }}
              >
                {content}
              </ReactMarkdown>
            </article>
          </section>
        </div>
      </div>

      <Footer />
    </main>
  );
}
